// src/App.js
import React, { useState, useEffect } from 'react';
import { AuthProvider, useAuth } from './context/AuthContext';
import Layout from './components/Layout';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Orders from './pages/Orders';
import Finance from './pages/Finance';
import Invoices from './pages/Invoices';
import Receipts from './pages/Receipts';
import Printing from './pages/Printing';
import Logistics from './pages/Logistics';
import Audit from './pages/Audit';
import Reports from './pages/Reports';
import Settings from './pages/Settings';
import Staff from './pages/Staff';
import Chat from './pages/Chat';
import PriceList from './pages/PriceList';
import Expenses from './pages/Expenses';
import Website from './pages/Website';
import PublicHome from './pages/PublicHome';
import { getPipeline } from './services/api';

// Private URL for staff to reach the login form. There is no login button
// on the public site — staff must visit this exact path (e.g.
// https://yoursite.com/login/admin). The web server serves the SPA for any
// path, so this works whether they type the URL or follow a private link.
const PRIVATE_LOGIN_PATH = '/login/admin';

const PAGES = {
  dashboard: Dashboard,
  orders:    Orders,
  finance:   Finance,
  invoices:  Invoices,
  receipts:  Receipts,
  printing:  Printing,
  logistics: Logistics,
  chat:      Chat,
  audit:     Audit,
  reports:   Reports,
  staff:     Staff,
  pricelist: PriceList,
  expenses:  Expenses,
  settings:  Settings,
  website:   Website,
};

function AppInner() {
  const { user, loading } = useAuth();
  const [page, setPage]           = useState('dashboard');
  const [alertCount, setAlertCount] = useState(0);
  // Public visitors land on the marketing home page first. Staff reach the
  // login form only via the hidden PRIVATE_LOGIN_PATH URL — no public button.
  const isLoginPath = typeof window !== 'undefined' &&
    window.location.pathname.toLowerCase().startsWith(PRIVATE_LOGIN_PATH);

  // Poll for alerts every 30s — uses the lightweight pipeline endpoint
  // (GROUP BY status) instead of downloading the entire order list.
  useEffect(() => {
    if (!user) return;
    const check = async () => {
      try {
        const r = await getPipeline();
        const statuses = r.data.data || [];
        const count = statuses
          .filter(s => ['AWAITING_APPROVAL', 'PAYMENT_PENDING', 'PACKAGED'].includes(s.status))
          .reduce((sum, s) => sum + (Number(s.count) || 0), 0);
        setAlertCount(count);
      } catch (_) {}
    };
    check();
    const interval = setInterval(check, 30000);
    return () => clearInterval(interval);
  }, [user]);

  if (loading) {
    return (
      <div style={{ minHeight:'100vh', background:'#0A0A0A', display:'flex', alignItems:'center', justifyContent:'center' }}>
        <div style={{ color:'#D4A017', fontSize:'14px', fontWeight:700, letterSpacing:'2px' }}>MASTER SOLVER...</div>
      </div>
    );
  }

  if (!user) {
    return isLoginPath
      ? <Login />
      : <PublicHome />;
  }

  // Staff logging in with a seeded/reset default password must change it
  // before they can reach anything else in the app.
  if (user.must_change_password) {
    return (
      <Layout page="settings" onNav={() => {}} alertCount={0} lockedNav>
        <div style={{
          background: '#2a1f00', border: '1px solid #D4A017', borderRadius: '8px',
          padding: '12px 16px', marginBottom: '16px', fontSize: '13px', color: '#D4A017',
        }}>
          ⚠ For security, you must set a new password before continuing.
        </div>
        <Settings />
      </Layout>
    );
  }

  const PageComponent = PAGES[page] || Dashboard;

  return (
    <Layout page={page} onNav={setPage} alertCount={alertCount}>
      <PageComponent />
    </Layout>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <AppInner />
    </AuthProvider>
  );
}
